import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Shield, Lock, Eye, Database, Cookie, Mail } from "lucide-react";

const sections = [
  {
    id: "introduction",
    icon: Shield,
    title: "Introduction",
    content: `Welcome to Softlume's Privacy Policy. This policy describes how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.`,
  },
  {
    id: "information-collection",
    icon: Database,
    title: "Information We Collect",
    content: `We collect information that you provide directly to us, including:
    
• Personal identification information (name, email address, phone number)
• Professional information (company name, job title, industry)
• Technical information (IP address, browser type, device information)
• Usage data (pages visited, time spent, interactions)
• Communication data (emails, chat messages, support tickets)

We collect this information when you:
• Fill out contact forms or request information
• Subscribe to our newsletter
• Engage with our services
• Communicate with our team`,
  },
  {
    id: "how-we-use",
    icon: Eye,
    title: "How We Use Your Information",
    content: `We use the information we collect for various purposes, including:

• Providing and maintaining our services
• Notifying you about changes to our services
• Providing customer support
• Gathering analysis to improve our services
• Monitoring the usage of our services
• Detecting and preventing technical issues
• Sending marketing communications (with your consent)
• Personalizing your experience
• Complying with legal obligations`,
  },
  {
    id: "data-protection",
    icon: Lock,
    title: "Data Protection & Security",
    content: `We implement appropriate technical and organizational security measures to protect your personal information:

• Encryption of data in transit and at rest
• Regular security audits and vulnerability assessments
• Access controls and authentication mechanisms
• Employee training on data protection
• Secure data centers with physical security
• Regular backups and disaster recovery plans

However, no method of transmission over the Internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.`,
  },
  {
    id: "cookies",
    icon: Cookie,
    title: "Cookies and Tracking",
    content: `We use cookies and similar tracking technologies to track activity on our website:

• Essential cookies: Required for the website to function
• Analytics cookies: Help us understand how visitors use our site
• Marketing cookies: Used to deliver relevant advertisements
• Preference cookies: Remember your settings and choices

You can instruct your browser to refuse all cookies or indicate when a cookie is being sent. However, some features may not function properly without cookies.`,
  },
  {
    id: "third-party",
    icon: Eye,
    title: "Third-Party Services",
    content: `We may employ third-party companies and individuals for various purposes:

• Service providers (hosting, analytics, email services)
• Payment processors
• Marketing platforms
• Customer support tools

These third parties have access to your information only to perform specific tasks on our behalf and are obligated not to disclose or use it for any other purpose.`,
  },
  {
    id: "your-rights",
    icon: Shield,
    title: "Your Rights",
    content: `You have certain rights regarding your personal information:

• Access: Request a copy of your personal data
• Correction: Request correction of inaccurate data
• Deletion: Request deletion of your data
• Restriction: Request restriction of processing
• Objection: Object to certain processing activities
• Portability: Request transfer of your data
• Withdraw consent: Withdraw consent at any time

To exercise these rights, please contact us using the information provided below.`,
  },
  {
    id: "data-retention",
    icon: Database,
    title: "Data Retention",
    content: `We retain your personal information only for as long as necessary:

• Active accounts: Duration of the business relationship
• Marketing data: Until you unsubscribe or object
• Legal requirements: As required by applicable laws
• Legitimate interests: As long as necessary for our purposes

When we no longer need your information, we will securely delete or anonymize it.`,
  },
  {
    id: "international",
    icon: Eye,
    title: "International Data Transfers",
    content: `Your information may be transferred to and maintained on computers located outside of your jurisdiction where data protection laws may differ. We ensure appropriate safeguards are in place for such transfers, including:

• Standard contractual clauses
• Privacy Shield certification (where applicable)
• Data processing agreements
• Adequate data protection measures`,
  },
  {
    id: "children",
    icon: Shield,
    title: "Children's Privacy",
    content: `Our services are not directed to individuals under 16. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.`,
  },
  {
    id: "changes",
    icon: Eye,
    title: "Changes to This Policy",
    content: `We may update this Privacy Policy from time to time. We will notify you of any changes by:

• Posting the new Privacy Policy on this page
• Updating the "Last Updated" date
• Sending an email notification (for significant changes)

We encourage you to review this Privacy Policy periodically for any changes.`,
  },
  {
    id: "contact",
    icon: Mail,
    title: "Contact Us",
    content: `If you have any questions about this Privacy Policy, please contact us:

Email: privacy@softlume.in
Phone: +91 7827782844
Address: Ghitorni, New Delhi 110030, India

We will respond to your inquiry within 30 days.`,
  },
];

export default function PrivacyPolicyPage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="privacy-policy-page"
    >
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-[50vh] flex items-center overflow-hidden pt-20"
      >
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/95 to-slate-950" />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 10, repeat: Infinity }}
            className="absolute top-1/3 left-1/3 w-96 h-96 bg-blue-500/5 rounded-full blur-[150px]"
          />
        </motion.div>
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative max-w-7xl mx-auto px-6 md:px-12 py-20"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block"
          >
            Legal
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight text-white mb-6"
          >
            Privacy <span className="glow-text">Policy</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-base md:text-lg text-slate-400 max-w-3xl leading-relaxed"
          >
            Last Updated: February 27, 2026
          </motion.p>
        </motion.div>
      </section>

      {/* Content */}
      <section className="relative py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <PolicySection key={section.id} section={section} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative py-16 bg-[#0a0f1e] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-slate-400 text-sm">
            Have questions about our privacy practices?{" "}
            <a
              href="mailto:privacy@softlume.in"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Contact our privacy team
            </a>
          </p>
        </div>
      </section>
    </motion.div>
  );
}

function PolicySection({ section, index }) {
  const ref = useRef(null);
  const Icon = section.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="group"
    >
      <div className="flex gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
            <Icon className="w-6 h-6 text-blue-400" />
          </div>
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
            {section.title}
          </h2>
          <div className="text-slate-400 leading-relaxed whitespace-pre-line">
            {section.content}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
